import React, { useEffect, useState } from 'react';
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import { Box, IconButton, TextField, MenuItem, FormControlLabel, RadioGroup, CircularProgress, Checkbox } from "@mui/material";
import { RootState, useAppDispatch, useAppSelector } from "../../../state/state.tsx";
import { closeModal } from "../../../state/ModalSlice/ModalSlice.ts";
import CloseIcon from "@mui/icons-material/Close";

import { addDoc, collection, doc, DocumentReference, Timestamp, getDoc } from 'firebase/firestore';

import { db, storage } from "../../../Config/firebase.ts"
import { toast } from 'react-toastify';
import { ref } from 'firebase/storage';
import { getDownloadURL } from 'firebase/storage';
import { uploadBytes } from 'firebase/storage';
import { selectEffectiveUID } from '../../../state/UserStatesV2/userSliceV2.ts';
import { BusinessProfile } from '../../Subscriptions/types/UserStateV2.ts';

interface FormData {
    name: string;
    number: string;
    role: "Administrator" | "Manager" | "Observer";
    selectedSubscriptions: DocumentReference[];
    assignmentStatus?: string;  // Added this field
}

interface DelegatedRole {
    name: string;
    number: string;
    role: string;
    serviceProviderReference: DocumentReference;
    businessName: string;
    assignedSubscriptions: DocumentReference[];
    userPhoto: string;
}

export default function AddTeamMemberModal() {
    // Modal Controllers
    const dispatch = useAppDispatch();
    const open = useAppSelector((state: RootState) => state.ModalSlice.AddTeamMemberModalOpen);
    const serviceProviderId = useAppSelector(selectEffectiveUID);  
    
    const [effectiveBusinessProfile, setEffectiveBusinessProfile] = useState<BusinessProfile | null>(null);
    const subscriptions = useAppSelector((state) => state.subscriptionTableSlice.data);
    const ForAddTeamMemberModal = useAppSelector((state) => state.ArbitrarySlice.ForAddTeamMemberModal);
    const userPhoneNumber = useAppSelector((state: RootState) => state.userSliceV2.data?.phone_number);

      
    // Component States
    const [formData, setFormData] = useState<FormData>({
        name: "",
        number: "",
        role: "Administrator",
        selectedSubscriptions: [],
        assignmentStatus: "Active"  // Added this field
    });

    const [profilePhotoFile, setProfilePhotoFile] = useState<File | null>(null)
    const [profilePhotoPreview, setProfilePhotoPreview] = useState<string | null>(null)

    const [errors, setErrors] = useState({
        name: false,
        number: false
    });

    const [isLoading, setIsLoading] = useState(false);
    const [ownerPhoneNumber, setOwnerPhoneNumber] = useState<string | null>(null);

    useEffect(() => {
        const fetchEffectiveBusinessProfile = async () => {
        const effectiveServiceProviderDoc = doc(db, `ServiceProviders`, serviceProviderId);
        const effectiveServiceProviderSnap = await getDoc(effectiveServiceProviderDoc);
        console.log("effectiveServiceProviderSnap", effectiveServiceProviderSnap.exists());
        if (effectiveServiceProviderSnap.exists()) {
            const effectiveServiceProviderData = effectiveServiceProviderSnap.data();
            setEffectiveBusinessProfile(effectiveServiceProviderData?.businessProfile);
        }
        }
        fetchEffectiveBusinessProfile();
       
    }, [serviceProviderId]);

    // UseEffect to populate the form with the data from the state
    useEffect(() => {
        if (!ForAddTeamMemberModal) return;
        
        setFormData({
            name: ForAddTeamMemberModal.name,
            number: ForAddTeamMemberModal.number?.startsWith('+91') ? ForAddTeamMemberModal.number.slice(3) : ForAddTeamMemberModal.number,
            role: ForAddTeamMemberModal.role,
            selectedSubscriptions: ForAddTeamMemberModal.selectedSubscriptions || [],
            assignmentStatus: "Active"  // Added this field
        });
        
        setProfilePhotoPreview(ForAddTeamMemberModal.userPhoto);
    }, [ForAddTeamMemberModal, open]);

    // UseEffect to fetch owner's phone number
    useEffect(() => {
        const fetchOwnerPhoneNumber = async () => {
            if (!serviceProviderId) return;
            
            try {
                const ownerRef = doc(db, `ServiceProviders/${serviceProviderId}`);
                const ownerSnap = await getDoc(ownerRef);
                
                if (ownerSnap.exists()) {
                    const ownerData = ownerSnap.data();
                    setOwnerPhoneNumber(ownerData?.phone_number || null);
                }
            } catch (error) {
                console.error("Error fetching owner phone number:", error);
            }
        };
        
        if (open) {
            fetchOwnerPhoneNumber();
        }
    }, [serviceProviderId, open]);

    // handler Functions

    const handleProfilePhotoChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files ? event.target.files[0] : null;

        if (file) {
            if (file.size > 5 * 1024 * 1024) {
                toast.error("Profile Photo must be less than 5mb size.")
                return;
            }
            setProfilePhotoFile(file)
            const reader = new FileReader();
            reader.onloadend = () => {
                setProfilePhotoPreview(reader.result as string);
            };
            reader.readAsDataURL(file);
        }
    };

    const handlePhoneNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value.replace(/[^0-9]/g, '');
        if (value.length <= 10) {
            setFormData(prev => ({ ...prev, number: value }));
        }
    }

    const handleClose = () => {
        dispatch(closeModal("AddTeamMemberModalOpen"));
        resetForm();
    };

    const handleSubscriptionChange = (subscriptionRef: DocumentReference, checked: boolean) => {
        setFormData(prev => {
            const newSubscriptions = checked
                ? [...prev.selectedSubscriptions, subscriptionRef]
                : prev.selectedSubscriptions.filter(ref => ref.path !== subscriptionRef.path);

            return {
                ...prev,
                selectedSubscriptions: newSubscriptions
            };
        });
    };

    // Create an array of subscriptions with references
    const subscriptionsWithReference = subscriptions?.map(subscription => ({
        ...subscription,
        reference: doc(db, `ServiceProviders/${serviceProviderId}/Subscriptions/${subscription.id}`)
    }));

    const handleProfilePhotoUpload = async (): Promise<string> => {
        if (profilePhotoFile) {
            const storageRef = ref(storage, `users/${serviceProviderId}/uploads/${profilePhotoFile.name}`);
            await uploadBytes(storageRef, profilePhotoFile);
            return getDownloadURL(storageRef);
        }
        return '';
    };

    // Error and Reset Functions

    const resetForm = () => {
        setFormData({
            name: "",
            number: "",
            role: "Administrator",
            selectedSubscriptions: [],
            assignmentStatus: "Active"  // Added this field
        });
        setProfilePhotoFile(null)
        setProfilePhotoPreview(null)
        setErrors({
            name: false,
            number: false
        });
    };

    const validateForm = (): boolean => {
       const formattedUserPhone = userPhoneNumber?.replace('+91', '');
        
        // Check if user is trying to add themselves    
        if (formData.number === formattedUserPhone) {
            toast.error("You cannot add yourself as a team member");
            return false;
        }
        
        // Check if user is trying to add the owner
        const formattedOwnerPhone = ownerPhoneNumber?.replace('+91', '');
        if (formData.number === formattedOwnerPhone) {
            toast.error("You cannot assign a role to the business owner");
            return false;
        }
        
        const newErrors = {
            name: formData.name.trim() === "",
            number: formData.number.length !== 10
        };
        setErrors(newErrors);
        return !Object.values(newErrors).some(error => error);
    };

    // Submit Operation
    const handleSubmit = async () => {
        if (!validateForm()) return;

        if ((formData.role === "Manager" || formData.role === "Observer") && formData.selectedSubscriptions.length === 0) {
            toast.error(`Please select at least one subscription for the ${formData.role}`);
            return;
        }

        setIsLoading(true);
        try {
            let profilePhotoUrl = profilePhotoPreview || "";
            if (profilePhotoFile) {
                profilePhotoUrl = await handleProfilePhotoUpload();
            }

            const phoneNumber = "+91" + formData.number;
            const currentTime = Timestamp.now();

            const spRef = doc(db, `ServiceProviders/${serviceProviderId}`);

            // Prepare the role data
            const roleData = {
                businessName: effectiveBusinessProfile?.businessName,
                name: formData.name,
                number: phoneNumber,
                role: formData.role,
                serviceProviderReference: spRef,
                userPhoto: profilePhotoUrl,
                assignedSubscriptions: formData.selectedSubscriptions
            } as DelegatedRole;

            if (ForAddTeamMemberModal.actionType === "editRole") {
                // Revoke old role first
                const oldRole = ForAddTeamMemberModal.oldRole;
                if (oldRole) {
                    const revokeRoleAction = {
                        actionType: "revokeRole",
                        businessName: effectiveBusinessProfile?.businessName,
                        businessType: effectiveBusinessProfile?.businessType,
                        modifiedTime: currentTime,
                        owner: spRef,
                        ownerName: effectiveBusinessProfile?.businessName,
                        roleAssigned: {
                            ...oldRole,
                            assignmentStatus: "Removed"
                        },
                        status: "pending",
                        userName: oldRole.name,
                        userNumber: oldRole.number
                    };
                    await addDoc(collection(db, "PendingActions"), revokeRoleAction);
                }
                // Grant new role
                const grantRoleAction = {
                    actionType: "grantRole",
                    businessName: effectiveBusinessProfile?.businessName,
                    businessType: effectiveBusinessProfile?.businessType,
                    modifiedTime: currentTime,
                    owner: spRef,
                    ownerName: effectiveBusinessProfile?.businessName,
                    roleAssigned: {
                        ...roleData,
                        assignmentStatus: "Active"
                    },
                    status: "pending",
                    userName: formData.name,
                    userNumber: phoneNumber
                };
                await addDoc(collection(db, "PendingActions"), grantRoleAction);
                toast.success("Role change requested! Pending approval.");
            } else {
                // Handle new role creation
                const grantRoleData = {
                    actionType: "grantRole",
                    businessName: effectiveBusinessProfile?.businessName,
                    businessType: effectiveBusinessProfile?.businessType,
                    modifiedTime: currentTime,
                    owner: spRef,
                    ownerName: effectiveBusinessProfile?.businessName,
                    roleAssigned: {
                        ...roleData,
                        assignmentStatus: "Active"
                    },
                    status: "pending",
                    userName: formData.name,
                    userNumber: phoneNumber
                };
                await addDoc(collection(db, "PendingActions"), grantRoleData);
                toast.success("New staff member added successfully!");
            }


            dispatch(closeModal("AddTeamMemberModalOpen"));

        } catch (error) {
            console.error("Error managing staff member:", error);
            toast.error("Failed to manage staff member. Please try again.");
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Dialog
            sx={{
                '& .MuiPaper-root': {
                    borderRadius: '10px',
                    width: { xs: 'calc(100% - 32px)', sm: 'auto' },
                    maxWidth: { xs: 'calc(100% - 32px)', sm: '600px', md: '800px' },
                    minWidth: { sm: '600px', md: '800px' },
                    margin: { xs: '16px', sm: 'auto' },
                    maxHeight: { xs: 'calc(100vh - 32px)', sm: '90vh' },
                    overflow: 'hidden',
                    transition: 'opacity 225ms cubic-bezier(0.4, 0, 0.2, 1) 0ms, transform 225ms cubic-bezier(0.4, 0, 0.2, 1) 0ms !important'
                },
            }}
            maxWidth="md"
            fullWidth
            open={open}
            onClose={handleClose}
        >
            <DialogContent sx={{ 
                paddingX: { xs: "16px", sm: "24px", md: "60px" }, 
                paddingY: { xs: "30px", sm: "40px", md: "50px" },
                maxHeight: { xs: 'calc(100vh - 32px)', sm: '90vh' },
                minHeight: { sm: '20vh' },
                overflowY: 'auto'
            }} className="bg-white">
                <div className="w-full max-w-full">
                    <div className="header pb-3 sm:pb-[20px]">
                        <p className="text-lg sm:text-cf_heading font-[600]">
                            {ForAddTeamMemberModal.oldRole ? "Edit Team Member" : "Add Team Member"}
                        </p>
                    </div>

                    <div className="absolute right-2 sm:right-4 top-2 sm:top-4">
                        <IconButton onClick={handleClose} sx={{ padding: { xs: '4px', sm: '8px' } }}>
                            <CloseIcon sx={{ fontSize: { xs: '18px', sm: '24px' } }} />
                        </IconButton>
                    </div>

                    <Box className="space-y-3 sm:space-y-4">
                        <div className="flex flex-col sm:flex-row items-center gap-3 sm:gap-5">
                            <img
                                className="inline-block size-20 sm:size-24 rounded-full shadow-lg ring-offset-2 ring-2 ring-cc_modal-grey-border flex-shrink-0"
                                src={profilePhotoPreview ? profilePhotoPreview : "https://firebasestorage.googleapis.com/v0/b/coachprodev.appspot.com/o/Config%2FProfilePhoto2Update.png?alt=media&token=53ea668c-04f3-493a-911d-15eba1ce897c"}
                                alt="Profile" />
                            <div className="w-full sm:w-auto">
                                <div>
                                    <input
                                        type="file"
                                        id="upload-photo"
                                        accept="image/*"
                                        style={{ display: 'none' }}
                                        onChange={handleProfilePhotoChange}
                                    />
                                    <label htmlFor="upload-photo"
                                        className="py-1.5 sm:py-2 px-2 sm:px-3 inline-flex items-center gap-x-2 text-xs sm:text-sm font-medium rounded-lg border border-gray-200 bg-white text-gray-800 shadow-sm hover:bg-gray-50 disabled:opacity-50 disabled:pointer-events-none cursor-pointer w-full sm:w-auto justify-center">
                                        <svg className="flex-shrink-0 size-3 sm:size-4"
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="20" height="20" viewBox="0 0 24 24" fill="none"
                                            stroke="currentColor" strokeWidth="2" strokeLinecap="round"
                                            strokeLinejoin="round">
                                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                            <polyline points="17 8 12 3 7 8" />
                                            <line x1="12" x2="12" y1="3" y2="15" />
                                        </svg>
                                        Upload photo
                                    </label>
                                </div>
                            </div>
                        </div>

                        <TextField
                            label="Name"
                            value={formData.name}
                            onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                            error={errors.name}
                            helperText={errors.name ? "Name is required" : ""}
                            size="small"
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    '& fieldset': {
                                        borderColor: 'grey', borderRadius: '10px',
                                    }, '&:hover fieldset': {
                                        borderColor: 'grey',
                                    }, '&.Mui-focused fieldset': {
                                        borderColor: 'skyblue',
                                    }, '& .MuiInputBase-input': {
                                        fontSize: '14px',
                                    },
                                }, '& .MuiFormHelperText-root': {
                                    color: errors.name ? 'red' : 'green',
                                }, fontWeight: 400, width: '100%',
                            }}
                        />

                        <TextField
                            label="Phone Number"
                            value={formData.number}
                            onChange={handlePhoneNumberChange}
                            error={errors.number}
                            helperText={errors.number ? "Enter valid phone number with 10 digits" : ""}
                            size="small"
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    '& fieldset': {
                                        borderColor: 'grey', borderRadius: '10px',
                                    }, '&:hover fieldset': {
                                        borderColor: 'grey',
                                    }, '&.Mui-focused fieldset': {
                                        borderColor: 'skyblue',
                                    }, '& .MuiInputBase-input': {
                                        fontSize: { xs: '14px', sm: '14px' },
                                    },
                                }, '& .MuiFormHelperText-root': {
                                    color: errors.number ? 'red' : 'green',
                                    fontSize: { xs: '0.7rem', sm: '0.75rem' },
                                }, fontWeight: 400, width: '100%',
                            }}
                        />

                        <TextField
                            select
                            label="Role"
                            value={formData.role}
                            onChange={(e) => setFormData(prev => ({
                                ...prev,
                                role: e.target.value as "Administrator" | "Manager" | "Observer",
                                selectedSubscriptions: []
                            }))}
                            size="small"
                            helperText={
                                formData.role === "Administrator" 
                                    ? "Admin can manage everything except bank account and roles." 
                                    : formData.role === "Manager"
                                        ? "Manager manages only subscriptions assigned to him by business owner."
                                        : "Observer can only mark attendance and track progress of students assigned to them."
                            }
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    '& fieldset': {
                                        borderColor: 'grey', borderRadius: '10px',
                                    }, '&:hover fieldset': {
                                        borderColor: 'grey',
                                    }, '&.Mui-focused fieldset': {
                                        borderColor: 'skyblue',
                                    }, '& .MuiInputBase-input': {
                                        fontSize: { xs: '14px', sm: '14px' },
                                    },
                                },
                                '& .MuiFormHelperText-root': {
                                    fontSize: { xs: '0.7rem', sm: '0.75rem' },
                                },
                                fontWeight: 400, width: '100%',
                            }}
                        >
                            <MenuItem value="Administrator">Administrator</MenuItem>
                            <MenuItem value="Manager">Manager</MenuItem>
                            <MenuItem value="Observer">Observer</MenuItem>
                        </TextField>

                        {(formData.role === "Manager" || formData.role === "Observer") && (
                            <div className="mt-3 sm:mt-4">
                                <p className="text-xs sm:text-sm font-medium mb-2">Select Subscriptions</p>
                                <RadioGroup>
                                    {subscriptionsWithReference?.map((subscription, index) => (
                                        <FormControlLabel
                                            key={index}
                                            value={subscription.id}
                                            control={
                                                <Checkbox
                                                    checked={formData.selectedSubscriptions.some(ref => ref.path === subscription.reference.path)}
                                                    onChange={(e) => handleSubscriptionChange(subscription.reference, e.target.checked)}
                                                    sx={{ padding: { xs: '4px', sm: '9px' } }}
                                                />
                                            }
                                            label={<span className="text-xs sm:text-sm">{subscription.Name}</span>}
                                            sx={{ marginBottom: { xs: '4px', sm: '8px' } }}
                                        />
                                    ))}
                                </RadioGroup>
                            </div>
                        )}

                        <div className={"flex flex-col sm:flex-row gap-2 pt-3 sm:pt-4"}>
                            <button disabled={false} onClick={(e) => {
                                e.preventDefault();
                                handleClose()
                            }}
                                className={`w-full bg-cc_bg-tertiary border border-solid border-gray-500 p-1.5 sm:p-1.5 rounded-lg text-xs sm:text-sm`}>Cancel
                            </button>

                            <button
                                onClick={handleSubmit}
                                disabled={isLoading}
                                className={`w-full bg-cc_brand text-white p-1.5 sm:p-1.5 rounded-lg border-gray-500 flex justify-center items-center text-xs sm:text-sm ${isLoading ? "cursor-not-allowed" : "cursor-pointer"}`}>
                                {isLoading && <CircularProgress sx={{color: "white", width: '16px !important', height: '16px !important'}} />}
                                <p className={"ml-2"}>{isLoading ? "Loading" : "Confirm"}</p>
                            </button>
                        </div>
                    </Box>
                </div>
            </DialogContent>
        </Dialog>
    );
} 